/**
 * Data de nascimento digitada como DD/MM/AAAA (teclado numérico, barras
 * automáticas) e gravada como YYYY-MM-DD, o formato que a Action
 * `pre-user-registration-onboarding` grava em `user_metadata.dateOfBirth`.
 */

export type BirthDateError =
  | 'DATE_OF_BIRTH_REQUIRED'
  | 'DATE_OF_BIRTH_INVALID'
  | 'DATE_OF_BIRTH_FUTURE'
  | 'DATE_OF_BIRTH_TOO_OLD'

/** Limite superior de idade: barra ano digitado errado sem impor idade mínima. */
const MAX_AGE_YEARS = 120

const DISPLAY = /^(\d{2})\/(\d{2})\/(\d{4})$/
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/** Máscara DD/MM/AAAA. Data ISO (colada ou do autofill) é convertida. */
export function formatBirthDateInput(value: string): string {
  if (ISO.test(value.trim())) return isoToBirthDate(value.trim())
  const digits = value.replace(/\D/g, '').slice(0, 8)
  const day = digits.slice(0, 2)
  const month = digits.slice(2, 4)
  const year = digits.slice(4)
  return [day, month, year].filter(Boolean).join('/')
}

export function birthDateToIso(display: string): string {
  const match = DISPLAY.exec(display.trim())
  return match ? `${match[3]}-${match[2]}-${match[1]}` : ''
}

export function isoToBirthDate(iso: string): string {
  const match = ISO.exec(iso.trim())
  return match ? `${match[3]}/${match[2]}/${match[1]}` : ''
}

function toIso(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export function validateBirthDate(
  display: string,
  today = new Date(),
): BirthDateError | null {
  if (!display.trim()) return 'DATE_OF_BIRTH_REQUIRED'
  const match = DISPLAY.exec(display.trim())
  if (!match) return 'DATE_OF_BIRTH_INVALID'
  const [day, month, year] = [match[1], match[2], match[3]].map(Number)
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return 'DATE_OF_BIRTH_INVALID'
  }
  const iso = toIso(date)
  if (iso > toIso(today)) return 'DATE_OF_BIRTH_FUTURE'
  const oldest = new Date(today)
  oldest.setFullYear(today.getFullYear() - MAX_AGE_YEARS)
  if (iso < toIso(oldest)) return 'DATE_OF_BIRTH_TOO_OLD'
  return null
}
